import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Skeleton,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Headphones as HeadphonesIcon,
  MenuBook as ReadingIcon,
  FactCheck as AttendanceIcon,
  Assessment as AssessmentIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Quiz as QuizIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import {
  useLazyGetClassesQuery,
  useLazyGetAdminExerciseReportQuery,
  useLazyGetAdminMockTestReportQuery,
} from "@/store/api/adminApi";
import PublicMockTestReport from "./PublicMockTestReport";

const sectionConfig = {
  "lc-exercise": {
    label: "LC",
    fullLabel: "Listening Comprehension",
    icon: <HeadphonesIcon fontSize="small" />,
    color: "#4facfe",
  },
  "rc-exercise": {
    label: "RC",
    fullLabel: "Reading Comprehension",
    icon: <ReadingIcon fontSize="small" />,
    color: "#43e97b",
  },
  "attendance-test": {
    label: "Attendance",
    fullLabel: "Attendance Test",
    icon: <AttendanceIcon fontSize="small" />,
    color: "#f093fb",
  },
};

const LearningStatusChip = ({ status }) => {
  const config = {
    completed: { label: "Completed", color: "#2e7d32", bg: "#e8f5e9" },
    not_completed: { label: "Not Completed", color: "#ed6c02", bg: "#fff3e0" },
    undone: { label: "Undone", color: "#9e9e9e", bg: "#f5f5f5" },
  };
  const c = config[status] || config.undone;
  return (
    <Chip
      label={c.label}
      size="small"
      sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getScoreColor = (value) => {
  if (value == null) return "text.secondary";
  if (value >= 70) return "success.main";
  if (value >= 50) return "warning.main";
  return "error.main";
};

// ========================
// EXERCISE REPORT TAB
// ========================
const ExerciseReportView = ({ data, isLoading, error }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load exercise report.</Alert>;
  }

  if (!data) {
    return (
      <Box textAlign="center" py={6}>
        <SchoolIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography color="text.secondary">Select a class to view exercise reports.</Typography>
      </Box>
    );
  }

  const { report, days } = data;

  if (!report || report.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <AssessmentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography color="text.secondary">No exercise data available for this class.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {report.map((studentReport) => (
        <Accordion
          key={studentReport.student._id}
          sx={{
            mb: 1.5,
            "&:before": { display: "none" },
            boxShadow: "none",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: "#fafafa" }}
          >
            <Box display="flex" alignItems="center" gap={1.5} width="100%">
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: 14 }}>
                {(studentReport.student.name || studentReport.student.username || "?")[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {studentReport.student.name || studentReport.student.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {studentReport.student.email}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {studentReport.days.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                No exercise days assigned.
              </Typography>
            ) : (
              studentReport.days.map((day) => (
                <Box key={day.exerciseDay} sx={{ mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Chip label={`Day ${day.dayNumber}`} size="small" color="primary" sx={{ fontWeight: 600 }} />
                    <Typography variant="subtitle2" fontWeight={500}>
                      {day.exerciseDayTitle}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                      {day.exerciseTitle}
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                          <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Attempts</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Best %</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Latest %</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Last Completed</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {day.sections.map((section, idx) => {
                          const cfg = sectionConfig[section.sectionType] || {
                            label: section.sectionType,
                            fullLabel: section.sectionType,
                            color: "#667eea",
                          };
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: cfg.color,
                                    }}
                                  />
                                  <Typography variant="body2">{cfg.fullLabel}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body2"
                                  fontWeight={500}
                                  color={
                                    section.totalAttempts < 3 &&
                                    section.totalAttempts > 0 &&
                                    section.sectionType === "lc-exercise"
                                      ? "error.main"
                                      : "text.primary"
                                  }
                                >
                                  {section.totalAttempts}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color={getScoreColor(section.bestPercentage)}
                                >
                                  {section.bestPercentage != null ? `${Math.round(section.bestPercentage)}%` : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  color={getScoreColor(section.latestPercentage)}
                                >
                                  {section.latestPercentage != null ? `${Math.round(section.latestPercentage)}%` : "—"}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(section.lastCompletedAt)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <LearningStatusChip status={section.learningStatus} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ========================
// MOCK TEST REPORT TAB
// ========================
const MockTestReportView = ({ data, isLoading, error }) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Box>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load mock test report.</Alert>;
  }

  if (!data) {
    return (
      <Box textAlign="center" py={6}>
        <SchoolIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography color="text.secondary">Select a class to view mock test reports.</Typography>
      </Box>
    );
  }

  const { report, exams } = data;

  if (!report || report.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <TrophyIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography color="text.secondary">No mock test data available for this class.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {report.map((studentReport) => (
        <Accordion
          key={studentReport.student._id}
          sx={{
            mb: 1.5,
            "&:before": { display: "none" },
            boxShadow: "none",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ backgroundColor: "#fafafa" }}
          >
            <Box display="flex" alignItems="center" gap={1.5} width="100%">
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main, fontSize: 14 }}>
                {(studentReport.student.name || studentReport.student.username || "?")[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {studentReport.student.name || studentReport.student.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {studentReport.student.email}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            {studentReport.exams.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                No exams assigned.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Exam</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Attempts</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Best Score</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Latest Score</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>L / R (Latest)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Last Completed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentReport.exams.map((examEntry, idx) => {
                      const hasAttempt = examEntry.totalAttempts > 0;
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {examEntry.exam.title}
                            </Typography>
                            {examEntry.exam.passingScore && (
                              <Typography variant="caption" color="text.secondary">
                                Passing: {examEntry.exam.passingScore}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" fontWeight={500}>
                              {examEntry.totalAttempts}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {hasAttempt ? (
                              <Typography variant="body2" fontWeight={700} color="primary.main">
                                {examEntry.bestScore ?? "—"}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {hasAttempt ? (
                              <Typography variant="body2" fontWeight={600}>
                                {examEntry.latestScore ?? "—"}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {hasAttempt ? (
                              <Typography variant="body2">
                                {examEntry.latestListeningScore ?? "—"} / {examEntry.latestReadingScore ?? "—"}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(examEntry.lastCompletedAt)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

// ========================
// MAIN REPORTS PAGE
// ========================
const AdminReports = () => {
  const theme = useTheme();
  const [selectedClass, setSelectedClass] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [allClasses, setAllClasses] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  const [
    getClasses,
    { data: classesData, isLoading: classesLoading, isFetching: classesFetching },
  ] = useLazyGetClassesQuery();
  const [
    getExerciseReport,
    { data: exerciseReportRaw, isLoading: exerciseLoading, error: exerciseError },
  ] = useLazyGetAdminExerciseReportQuery();
  const [
    getMockTestReport,
    { data: mockTestReportRaw, isLoading: mockTestLoading, error: mockTestError },
  ] = useLazyGetAdminMockTestReportQuery();

  const exerciseReport = exerciseReportRaw?.data || null;
  const mockTestReport = mockTestReportRaw?.data || null;

  // Load initial classes
  useEffect(() => {
    getClasses({ page: 1, limit: 20 });
  }, [getClasses]);

  // Update classes list when data arrives
  useEffect(() => {
    if (classesData?.data?.classes) {
      const newClasses = classesData.data.classes;
      setAllClasses((prev) => {
        if (page === 1) return newClasses;
        const existingIds = new Set(prev.map((c) => c._id));
        const uniqueNew = newClasses.filter((c) => !existingIds.has(c._id));
        return [...prev, ...uniqueNew];
      });
      const pagination = classesData.data.pagination;
      setHasMore(pagination && pagination.current < pagination.pages);
    }
  }, [classesData, page]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setAllClasses([]);
      getClasses({ page: 1, limit: 20, search: searchInput });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, getClasses]);

  const handleClassChange = (event, newValue) => {
    setSelectedClass(newValue);
    if (newValue?._id) {
      getExerciseReport(newValue._id);
      getMockTestReport(newValue._id);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (hasMore && !classesFetching) {
      const nextPage = page + 1;
      setPage(nextPage);
      getClasses({ page: nextPage, limit: 20, search: searchInput });
    }
  }, [hasMore, classesFetching, page, searchInput, getClasses]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "white",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <AssessmentIcon sx={{ fontSize: 36 }} />
          <Typography variant="h4" fontWeight={700}>
            Class Reports
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
          View exercise and mock test reports per class, student, and section
        </Typography>
      </Box>

      {/* Class Selector */}
      {tabValue !== 2 && (
        <Card sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <Autocomplete
          fullWidth
          options={allClasses}
          value={selectedClass}
          onChange={handleClassChange}
          onInputChange={(e, value) => setSearchInput(value)}
          getOptionLabel={(option) => `${option.classCode || ''} — ${option.className || ''}`}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          loading={classesLoading || classesFetching}
          noOptionsText={searchInput ? "No classes found" : "Type to search classes"}
          ListboxProps={{
            onScroll: (event) => {
              const listboxNode = event.currentTarget;
              if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
                handleLoadMore();
              }
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search and Select Class"
              placeholder="Type class code or name..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {classesFetching ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <SchoolIcon fontSize="small" color="primary" />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {option.classCode} — {option.className}
                  </Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </li>
          )}
        />
        </Card>
      )}

      {/* Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": { fontWeight: 600, textTransform: "none", fontSize: "0.95rem" },
            }}
          >
            <Tab
              icon={<AssessmentIcon />}
              iconPosition="start"
              label="Exercise Reports"
            />
            <Tab
              icon={<TrophyIcon />}
              iconPosition="start"
              label="Mock Test Reports"
            />
            <Tab
              icon={<QuizIcon />}
              iconPosition="start"
              label="Public Mock Tests"
            />
          </Tabs>
        </Box>

        <CardContent>
          {/* Class info */}
          {selectedClass && exerciseReport?.class && (
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <SchoolIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                {exerciseReport.class.classCode} — {exerciseReport.class.className}
              </Typography>
              {exerciseReport.students && (
                <Chip
                  size="small"
                  icon={<PersonIcon />}
                  label={`${exerciseReport.students.length} students`}
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          )}

          {tabValue === 0 && (
            <ExerciseReportView
              data={exerciseReport}
              isLoading={exerciseLoading}
              error={exerciseError}
            />
          )}
          {tabValue === 1 && (
            <MockTestReportView
              data={mockTestReport}
              isLoading={mockTestLoading}
              error={mockTestError}
            />
          )}
          {tabValue === 2 && <PublicMockTestReport />}
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminReports;
